/**
 * Groups project strings by issue type and key-locale pair.
 *
 * For duplicates: multiple records with the same key-locale are grouped together
 * For conflicts and missing locales: each record is its own group
 *
 * @param {Array} projectStrings - Array of project string objects
 * @returns {Object} Grouped issues: { duplicates, conflicts, missingLocales }
 */
const groupIssues = (projectStrings) => {
  if (!projectStrings || projectStrings.length === 0) {
    return { duplicates: [], conflicts: [], missingLocales: [] }
  }

  const duplicateGroups = {}
  const conflicts = []
  const missingLocales = []

  projectStrings.forEach((ps) => {
    // Missing locale takes precedence (locale is empty string)
    if (ps.locale === '') {
      missingLocales.push({
        type: 'missingLocale',
        key: ps.key,
        locale: ps.locale,
        records: [ps]
      })
      return
    }

    // Duplicates are grouped by key-locale
    if (ps.duplicated) {
      const groupKey = `${ps.key}|${ps.locale}`
      if (!duplicateGroups[groupKey]) {
        duplicateGroups[groupKey] = {
          type: 'duplicate',
          key: ps.key,
          locale: ps.locale,
          records: []
        }
      }
      duplicateGroups[groupKey].records.push(ps)
      return
    }

    // Conflicts are individual issues
    if (ps.conflict) {
      conflicts.push({
        type: 'conflict',
        key: ps.key,
        locale: ps.locale,
        records: [ps]
      })
    }
  })

  return {
    duplicates: Object.values(duplicateGroups),
    conflicts,
    missingLocales
  }
}

/**
 * Flattens grouped issues back into a single array for rendering
 * @param {Object} groupedIssues - Output from groupIssues()
 * @returns {Array} Flat array of issue groups
 */
const flattenGroupedIssues = (groupedIssues) => {
  const { duplicates, conflicts, missingLocales } = groupedIssues
  return [...duplicates, ...conflicts, ...missingLocales]
}

/**
 * Counts the total number of issues (not records)
 * @param {Object} groupedIssues - Output from groupIssues()
 * @returns {number} Total issue count
 */
const countIssues = (groupedIssues) => {
  const { duplicates, conflicts, missingLocales } = groupedIssues
  return duplicates.length + conflicts.length + missingLocales.length
}

export { groupIssues, flattenGroupedIssues, countIssues }
export default groupIssues
